/**
 * App.tsx — route table. Every sidebar item is its own route; there are no pages inside pages. Manager-only screens are
 * wrapped in <RequireManager>, the rest need <RequireAuth>. This is UX convenience only: the Worker enforces the real
 * RBAC on every request. Old addresses (/ads?view=…, /ai/brand-kit, /settings/…) are redirected so bookmarks still work.
 * Each page is its own chunk (React.lazy); the <Suspense> boundary lives inside <Layout> around the page outlet.
 */
import { lazy } from "react";
import { Navigate, Route, Routes, useLocation, useSearchParams } from "react-router-dom";
import { Layout } from "./components/Layout";
import { RequireAuth, RequireCapability, RequireManager } from "./components/ProtectedRoute";
import { Login } from "./pages/Login";
import { ForgotPassword } from "./pages/ForgotPassword";
import { ResetPassword } from "./pages/ResetPassword";
import { loaders } from "./routeLoaders";

const Review = lazy(loaders.review);
const AdsThisWeek = lazy(loaders.adsThisWeek);
const AdsConcepts = lazy(loaders.adsConcepts);
const AdsList = lazy(loaders.adsList);
const AdsResearch = lazy(loaders.adsResearch);
const AdsGoogle = lazy(loaders.adsGoogle);
const AdsResults = lazy(loaders.adsResults);
const AdsContext = lazy(loaders.adsContext);
const SocialThisWeek = lazy(loaders.socialThisWeek);
const SocialIdeas = lazy(loaders.socialIdeas);
const SocialPosts = lazy(loaders.socialPosts);
const SocialChannels = lazy(loaders.socialChannels);
const SocialResults = lazy(loaders.socialResults);
const SocialContext = lazy(loaders.socialContext);
const ContentList = lazy(loaders.contentList);
const ContentEditor = lazy(loaders.contentEditor);
const Calendar = lazy(loaders.calendar);
const Taxonomy = lazy(loaders.taxonomy);
const SiteGit = lazy(loaders.siteGit);
const AiIdeas = lazy(loaders.aiIdeas);
const AiDraftList = lazy(loaders.aiDraftList);
const AiDraftReview = lazy(loaders.aiDraftReview);
const BrandMemory = lazy(loaders.brandMemory);
const Evidence = lazy(loaders.evidence);
const OpsRuns = lazy(loaders.opsRuns);
const OpsCosts = lazy(loaders.opsCosts);
const OpsImports = lazy(loaders.opsImports);
const OpsIntegrations = lazy(loaders.opsIntegrations);
const AuditLog = lazy(loaders.auditLog);
const SettingsProducts = lazy(loaders.settingsProducts);
const SettingsPeople = lazy(loaders.settingsPeople);
const SettingsApiKeys = lazy(loaders.settingsApiKeys);
const SettingsPrompts = lazy(loaders.settingsPrompts);
const Security = lazy(loaders.security);

/** The old Ads address (/ads?view=…) goes to the page that now holds that view, keeping product and channel. */
function LegacyAds() {
  const [p] = useSearchParams();
  const keep = new URLSearchParams();
  for (const k of ["product", "channel"]) if (p.get(k)) keep.set(k, p.get(k)!);
  const view = p.get("view") ?? "home";
  if (view === "review") {
    keep.set("queue", "ads");
    if (p.get("ad")) keep.set("ad", p.get("ad")!);
    return <Navigate to={`/review?${keep}`} replace />;
  }
  const to = { home: "this-week", all: "ads", ideas: "concepts", research: "research", google: "google", results: "results", settings: "context" }[view] ?? "this-week";
  const q = keep.toString();
  return <Navigate to={`/ads/${to}${q ? `?${q}` : ""}`} replace />;
}

/** Keeps the query string (product, channel) when an old address is redirected. */
function Redirect({ to }: { to: string }) {
  const { search } = useLocation();
  return <Navigate to={`${to}${to.includes("?") ? search.replace("?", "&") : search}`} replace />;
}

export function App() {
  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route
        element={
          <RequireAuth>
            <Layout />
          </RequireAuth>
        }
      >
        <Route path="/" element={<Navigate to="/review" replace />} />
        <Route path="/review" element={<RequireCapability capability="content.publish"><Review /></RequireCapability>} />

        {/* Ads */}
        <Route path="/ads" element={<RequireCapability capability="ai.use"><LegacyAds /></RequireCapability>} />
        <Route path="/ads/this-week" element={<RequireCapability capability="ai.use"><AdsThisWeek /></RequireCapability>} />
        <Route path="/ads/concepts" element={<RequireCapability capability="ai.use"><AdsConcepts /></RequireCapability>} />
        <Route path="/ads/ads" element={<RequireCapability capability="ai.use"><AdsList /></RequireCapability>} />
        <Route path="/ads/research" element={<RequireCapability capability="ai.use"><AdsResearch /></RequireCapability>} />
        <Route path="/ads/google" element={<RequireCapability capability="ai.use"><AdsGoogle /></RequireCapability>} />
        <Route path="/ads/results" element={<RequireCapability capability="ai.use"><AdsResults /></RequireCapability>} />
        <Route path="/ads/context" element={<RequireCapability capability="ai.use"><AdsContext /></RequireCapability>} />

        {/* Social */}
        <Route path="/social" element={<Navigate to="/social/this-week" replace />} />
        <Route path="/social/this-week" element={<SocialThisWeek />} />
        <Route path="/social/ideas" element={<SocialIdeas />} />
        <Route path="/social/posts" element={<SocialPosts />} />
        <Route path="/social/channels" element={<SocialChannels />} />
        <Route path="/social/results" element={<SocialResults />} />
        <Route path="/social/context" element={<SocialContext />} />

        {/* Content */}
        <Route path="/content" element={<ContentList />} />
        <Route path="/content/new" element={<ContentEditor />} />
        <Route path="/content/taxonomy" element={<RequireCapability capability="taxonomy.manage"><Taxonomy /></RequireCapability>} />
        <Route path="/content/categories" element={<Redirect to="/content/taxonomy" />} />
        <Route path="/content/authors" element={<Redirect to="/content/taxonomy" />} />
        <Route path="/content/:id" element={<ContentEditor />} />
        <Route path="/calendar" element={<Calendar />} />
        <Route path="/git" element={<RequireManager><SiteGit /></RequireManager>} />
        <Route path="/ai/ideas" element={<RequireCapability capability="ai.use"><AiIdeas /></RequireCapability>} />
        <Route path="/ai/drafts" element={<RequireCapability capability="ai.use"><AiDraftList /></RequireCapability>} />
        <Route path="/ai/drafts/:id" element={<RequireCapability capability="ai.use"><AiDraftReview /></RequireCapability>} />
        <Route path="/ai/memory" element={<RequireCapability capability="ai.use"><BrandMemory /></RequireCapability>} />
        <Route path="/ai/evidence" element={<Evidence />} />

        {/* Operations */}
        <Route path="/ops/runs" element={<RequireCapability capability="ai.use"><OpsRuns /></RequireCapability>} />
        <Route path="/ops/costs" element={<RequireCapability capability="ai.use"><OpsCosts /></RequireCapability>} />
        <Route path="/ops/imports" element={<RequireCapability capability="ai.use"><OpsImports /></RequireCapability>} />
        <Route path="/ops/integrations" element={<RequireManager><OpsIntegrations /></RequireManager>} />
        <Route path="/audit" element={<RequireManager><AuditLog /></RequireManager>} />

        {/* Settings */}
        <Route path="/settings" element={<Navigate to="/settings/products" replace />} />
        <Route path="/settings/products" element={<RequireManager><SettingsProducts /></RequireManager>} />
        <Route path="/users" element={<RequireManager><SettingsPeople /></RequireManager>} />
        <Route path="/settings/api-keys" element={<RequireManager><SettingsApiKeys /></RequireManager>} />
        <Route path="/settings/prompts" element={<RequireManager><SettingsPrompts /></RequireManager>} />
        <Route path="/account/security" element={<Security />} />

        {/* Old addresses */}
        <Route path="/ai" element={<Navigate to="/review" replace />} />
        <Route path="/ai/brand-kit" element={<Redirect to="/ads/context" />} />
        <Route path="/ai/usage" element={<Navigate to="/ops/costs" replace />} />
        <Route path="/ai/pictures" element={<Navigate to="/settings/products" replace />} />
        <Route path="/settings/pictures" element={<Navigate to="/settings/products" replace />} />
        <Route path="/settings/permissions" element={<Navigate to="/users" replace />} />
        <Route path="/settings/notifications" element={<Navigate to="/users" replace />} />
        <Route path="/settings/taxonomy" element={<Navigate to="/content/taxonomy" replace />} />
        <Route path="/settings/sites" element={<Navigate to="/git" replace />} />
        <Route path="/settings/variables" element={<Navigate to="/git" replace />} />
        <Route path="/settings/connections" element={<Navigate to="/settings/api-keys?tab=status" replace />} />
        <Route path="/evidence" element={<Navigate to="/ai/evidence" replace />} />
      </Route>
    </Routes>
  );
}
